import React from "react";
import { Helmet } from "react-helmet-async";
import TopNavbar from "@/components/layout/TopNavbar";
import Footer from "@/components/home/Footer";
import { Button } from "@/components/ui/button";
import { OptimizedImage } from "@/components/ui";
import { Tent, Star, Calendar, Users } from "lucide-react";
import { Link } from 'react-router-dom';

const HERO_IMAGE_URL = "https://media.hivetivity.com/happy-children-playing-together-outdoors-dancing-around-grass-enjoying-outdoor-activities-having-fun-park-kids-party-friendship-concept.jpg";

const SummerCamps = () => {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Summer Camp Management Software - Hivetivity</title>
        <meta name="description" content="Streamline your summer camp operations with Hivetivity. Manage registrations, activities, and parent communications all in one place." />
      </Helmet>

      <TopNavbar />

      <main>
        {/* Hero Section */}
        <section className="relative py-20 bg-gradient-to-b from-blue-50 to-white">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="text-4xl md:text-5xl font-bold text-[#5a5a59] mb-6">
                  Run Your Summer Camp with Confidence
                </h1>
                <p className="text-xl text-gray-600 mb-8">
                  Focus on creating unforgettable summer experiences while we handle the administrative tasks with our comprehensive camp management platform.
                </p>
                <Link to="/business-trial">
                  <Button className="bg-[#4ba3c7] hover:bg-[#4ba3c7]/90 text-white px-8 py-6 text-lg">
                    Start Free Trial
                  </Button>
                </Link>
              </div>
              <div className="relative">
                <img
                  src={HERO_IMAGE_URL}
                  alt="Children playing together outdoors at summer camp"
                  width={600}
                  height={400}
                  className="rounded-2xl shadow-xl"
                  style={{ objectFit: 'cover', width: '100%', height: 'auto' }}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center text-[#5a5a59] mb-12">
              Everything Your Summer Camp Needs
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="p-6 rounded-xl bg-blue-50 hover:bg-blue-100 transition-colors">
                <Tent className="w-12 h-12 text-[#4ba3c7] mb-4" />
                <h3 className="text-xl font-semibold text-[#5a5a59] mb-2">Activity Planning</h3>
                <p className="text-gray-600">Organize and schedule daily activities, field trips, and special events.</p>
              </div>
              <div className="p-6 rounded-xl bg-blue-50 hover:bg-blue-100 transition-colors">
                <Users className="w-12 h-12 text-[#4ba3c7] mb-4" />
                <h3 className="text-xl font-semibold text-[#5a5a59] mb-2">Group Management</h3>
                <p className="text-gray-600">Easily manage camper groups, counselors, and activity assignments.</p>
              </div>
              <div className="p-6 rounded-xl bg-blue-50 hover:bg-blue-100 transition-colors">
                <Calendar className="w-12 h-12 text-[#4ba3c7] mb-4" />
                <h3 className="text-xl font-semibold text-[#5a5a59] mb-2">Smart Scheduling</h3>
                <p className="text-gray-600">Coordinate activities, staff rotations, and facility usage.</p>
              </div>
              <div className="p-6 rounded-xl bg-blue-50 hover:bg-blue-100 transition-colors">
                <Star className="w-12 h-12 text-[#4ba3c7] mb-4" />
                <h3 className="text-xl font-semibold text-[#5a5a59] mb-2">Parent Portal</h3>
                <p className="text-gray-600">Keep parents informed with updates, photos, and important announcements.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonial Section */}
        <section className="py-20 bg-blue-50">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl font-bold text-[#5a5a59] mb-8">
                Loved by Camp Directors
              </h2>
              <blockquote className="text-xl text-gray-600 italic mb-6">
                "Hivetivity has revolutionized how we run our summer camp. From registration to daily operations, everything is streamlined and efficient."
              </blockquote>
              <p className="font-semibold text-[#5a5a59]">
                - Michael Thompson, Adventure Camp Director
              </p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold text-[#5a5a59] mb-6">
              Ready to Transform Your Summer Camp?
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Join hundreds of successful summer camps using Hivetivity to create unforgettable experiences for their campers.
            </p>
            <Link to="/business-trial">
              <Button className="bg-[#4ba3c7] hover:bg-[#4ba3c7]/90 text-white px-8 py-6 text-lg">
                Start Your Free Trial Today
              </Button>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default SummerCamps;
